import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    TextField,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Backdrop,
    CircularProgress,
    Card,
    CardContent,
    Chip,
    IconButton,
    Tooltip,
} from "@mui/material";
import { RefreshSharp, Visibility, Phone } from "@mui/icons-material";
import axiosInstance from "../../api/axiosInstance";
import base_url from "../../constants";
import useNotification from "../common/Notification";
import { WB_SX, WB_UI } from "../../ui/ux/uiTokens";

const PhoneNumbers = () => {
    const [phoneNumbers, setPhoneNumbers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedPhone, setSelectedPhone] = useState(null);
    const showNotification = useNotification();

    const fetchPhoneNumbers = async () => {
        setLoading(true);
        try {
            // Backend will automatically use config from database based on authenticated user
            const response = await axiosInstance.get(
                `${base_url}/whatsapp-business/phone-numbers`,
                {
                    params: {
                        save_to_db: true,
                    },
                }
            );

            if (response.data?.data) {
                setPhoneNumbers(response.data.data);
                showNotification("Phone numbers fetched successfully!", "success");
            } else {
                setPhoneNumbers(response.data || []);
                showNotification("Phone numbers fetched successfully!", "success");
            }
        } catch (error) {
            console.error("Error fetching phone numbers:", error);
            showNotification(
                error.response?.data?.error || "Failed to fetch phone numbers",
                "error"
            );
        } finally {
            setLoading(false);
        }
    };

    const fetchPhoneDetails = async (phoneNumberId) => {
        setLoading(true);
        try {
            // Backend will automatically use config from database based on authenticated user
            const response = await axiosInstance.get(
                `${base_url}/whatsapp-business/phone-number/${phoneNumberId}`
            );

            setSelectedPhone(response.data);
            showNotification("Phone number details fetched successfully!", "success");
        } catch (error) {
            console.error("Error fetching phone details:", error);
            showNotification(
                error.response?.data?.error || "Failed to fetch phone details",
                "error"
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={WB_SX.page}>
            <Typography
                variant="h6"
                sx={WB_SX.pageTitle}
            >
                Phone Numbers
            </Typography>

            <Backdrop
                sx={(theme) => ({
                    color: "#fff",
                    zIndex: theme.zIndex.drawer + 1,
                })}
                open={loading}
            >
                <CircularProgress color="inherit" />
            </Backdrop>

            <Card sx={{ mb: WB_UI.spacing.sectionGap, ...WB_SX.card }}>
                <CardContent sx={WB_SX.cardContent}>
                    <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
                        <Button
                            variant="contained"
                            onClick={fetchPhoneNumbers}
                            startIcon={<Phone />}
                            sx={{ minWidth: "150px", ...WB_SX.primaryButton }}
                        >
                            Fetch Numbers
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={fetchPhoneNumbers}
                            startIcon={<RefreshSharp />}
                            sx={WB_SX.secondaryButton}
                        >
                            Refresh
                        </Button>
                    </Box>
                </CardContent>
            </Card>

            {phoneNumbers.length > 0 && (
                <TableContainer component={Paper} elevation={0} sx={WB_SX.tableContainer}>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: WB_UI.color.tableHeadBg }}>
                                <TableCell sx={WB_SX.tableHeadCell}>ID</TableCell>
                                <TableCell sx={WB_SX.tableHeadCell}>Phone Number</TableCell>
                                <TableCell sx={WB_SX.tableHeadCell}>Status</TableCell>
                                <TableCell sx={WB_SX.tableHeadCell}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {phoneNumbers.map((phone, index) => (
                                <TableRow key={phone.id || index} sx={WB_SX.tableRow}>
                                    <TableCell>{phone.id}</TableCell>
                                    <TableCell>
                                        {phone.display_phone_number || phone.verified_name || "N/A"}
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={phone.status || "UNKNOWN"}
                                            color={
                                                phone.status === "CONNECTED"
                                                    ? "success"
                                                    : phone.status === "PENDING"
                                                    ? "warning"
                                                    : "default"
                                            }
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Tooltip title="View Details">
                                            <IconButton
                                                size="small"
                                                onClick={() => fetchPhoneDetails(phone.id)}
                                                color="primary"
                                            >
                                                <Visibility />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {selectedPhone && (
                <Card sx={{ mt: 3, ...WB_SX.card }}>
                    <CardContent sx={WB_SX.cardContent}>
                        <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: "-0.02em", mb: 2 }}>
                            Phone Number Details
                        </Typography>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                            <Typography>
                                <strong>ID:</strong> {selectedPhone.id}
                            </Typography>
                            <Typography>
                                <strong>Display Number:</strong>{" "}
                                {selectedPhone.display_phone_number || "N/A"}
                            </Typography>
                            <Typography>
                                <strong>Verified Name:</strong>{" "}
                                {selectedPhone.verified_name || "N/A"}
                            </Typography>
                            <Typography>
                                <strong>Status:</strong>{" "}
                                <Chip
                                    label={selectedPhone.status || "UNKNOWN"}
                                    color={
                                        selectedPhone.status === "CONNECTED"
                                            ? "success"
                                            : "default"
                                    }
                                    size="small"
                                />
                            </Typography>
                            {selectedPhone.quality_rating && (
                                <Typography>
                                    <strong>Quality Rating:</strong>{" "}
                                    {selectedPhone.quality_rating}
                                </Typography>
                            )}
                        </Box>
                    </CardContent>
                </Card>
            )}
        </Box>
    );
};

export default PhoneNumbers;

